/**
 * Cross-Subdomain Tracking Utility
 *
 * This module provides utilities for tracking user activity across subdomains
 * by leveraging cookies that are shared across all subdomains (domain=.example.com).
 *
 * ## THE PROBLEM: Local Storage Isolation
 *
 * When a user interacts with a widget across multiple subdomains:
 *
 * 1. **User chats on main domain** (e.g., `www.example.com`):
 *    - Widget creates a conversation with `sessionId` and `lastActiveSessionId`
 *    - This data is stored in:
 *      - ✅ Local storage (domain-specific)
 *      - ✅ Cookies (shared via `domain=.example.com`) - ONLY when session is active
 *
 * 2. **User navigates to subdomain** (e.g., `start.example.com/signup`):
 *    - Local storage is **NOT shared** across subdomains
 *    - The analytics context loses `lastActiveSessionId` and `clientId`
 *    - Form submissions can't be attributed to the original conversation
 *
 * 3. **User submits form**:
 *    - Without cross-domain tracking, the `rw_client_form_submitted` event:
 *      - ❌ Missing `rw_last_active_session_id` (can't link to conversation)
 *      - ❌ Missing `rw_client_id` (can't link to traffic control decisions)
 *      - ❌ Lost attribution and conversion funnel tracking
 *
 * ## THE SOLUTION: Cookie-Based Fallback
 *
 * Cookies with `domain=.example.com` **ARE shared** across all subdomains:
 * - `rose_last_active_session`: The last session where user sent a message
 * - `rose_last_active_session_date`: Unix timestamp (milliseconds) when last session was active
 * - `rose_client_id`: Stable client ID for traffic control and tracking
 *
 * **IMPORTANT**: The `rose_client_id` cookie is ONLY set when there's an active session
 * (i.e., when `lastActiveSessionId` exists). This prevents PostHog's distinct_id on domain2
 * from overwriting the original clientId from domain1 when there's no active conversation.
 *
 * This utility provides a fallback mechanism:
 * 1. Check if tracking data exists in the analytics context (local storage)
 * 2. If missing, fall back to reading from cookies
 * 3. Enrich events with cross-domain data for proper attribution
 *
 * ## USAGE EXAMPLE
 *
 * ```typescript
 * // In PostHogProvider when building event properties for FORM_SUBMITTED:
 * const context = this.getContext();
 *
 * // Check if context is missing attribution data
 * if (!context.lastActiveSessionId || !context.clientId) {
 *     // Fall back to cross-domain cookies
 *     const crossDomainData = getCrossDomainTrackingData();
 *     if (crossDomainData) {
 *         properties.rw_last_active_session_id = crossDomainData.lastActiveSessionId;
 *         properties.rw_last_active_session_date = crossDomainData.lastActiveSessionDate;
 *         properties.rw_client_id = crossDomainData.clientId;
 *         logger.debug('Using cross-domain tracking data from cookies');
 *     }
 * }
 * ```
 *
 * ## TECHNICAL DETAILS
 *
 * **Cookie Names** (set by CookieProvider):
 * - `rose_last_active_session`: Last active session ID
 * - `rose_last_active_session_date`: Unix timestamp in milliseconds when session was last active
 * - `rose_client_id`: Stable client ID
 *
 * **Cookie Scope**:
 * - Domain: `.example.com` (works for all subdomains)
 * - Path: `/` (works for all paths)
 * - Secure: true (HTTPS only)
 * - SameSite: Lax
 * - Max-Age: 1 year
 *
 * **When to Use This**:
 * - ✅ Form submission events on different subdomains
 * - ✅ CTA click events crossing subdomains
 * - ✅ Any navigation event that may cross subdomains
 * - ❌ Regular widget events on the same subdomain (use context directly)
 */

import { getConsentMode } from '../services/consentMode';
import { logger } from '../utils/core/logger';

/**
 * Cross-domain tracking data retrieved from cookies
 */
interface CrossDomainTrackingData {
    lastActiveSessionId: string | null;
    clientId: string | null;
    lastActiveSessionDate: number | null; // Unix timestamp in milliseconds
}

/**
 * Cookie names used by the CookieProvider for cross-subdomain tracking
 */
const COOKIE_NAMES = {
    LAST_ACTIVE_SESSION: 'rose_last_active_session',
    LAST_ACTIVE_SESSION_DATE: 'rose_last_active_session_date',
    CLIENT_ID: 'rose_client_id',
} as const;

/**
 * Get a cookie value by name
 *
 * @param cookieName - Name of the cookie to retrieve
 * @returns Cookie value or null if not found
 */
function getCookieValue(cookieName: string): string | null {
    if (getConsentMode() !== 'full') return null;
    try {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === cookieName) {
                return decodeURIComponent(value);
            }
        }
        return null;
    } catch (error) {
        logger.error('Failed to read cookie:', error);
        return null;
    }
}

/**
 * Get cross-domain tracking data from cookies
 *
 * This function retrieves `lastActiveSessionId` and `clientId` from cookies
 * that are shared across all subdomains (domain=.example.com).
 *
 * **Use this when**:
 * - Tracking events on a different subdomain than where the conversation started
 * - The analytics context is missing `lastActiveSessionId` or `clientId`
 * - You need to maintain attribution across subdomain boundaries
 *
 * **Example Scenario**:
 * ```
 * 1. User chats on www.pennylane.com (creates sessionId abc123 at timestamp 1704067200000)
 * 2. User clicks CTA → navigates to start.pennylane.com/signup
 * 3. User fills out form and submits
 * 4. Form tracking calls this function to get sessionId and timestamp from cookies
 * 5. Event includes:
 *    - rw_last_active_session_id = abc123 (from cookie!)
 *    - rw_last_active_session_date = 1704067200000 (from cookie!)
 * 6. Conversion funnel properly attributes form to original conversation with timing data
 * ```
 *
 * @returns Object with lastActiveSessionId, lastActiveSessionDate, and clientId from cookies, or null for each if not found
 */
export function getCrossDomainTrackingData(): CrossDomainTrackingData {
    const lastActiveSessionId = getCookieValue(COOKIE_NAMES.LAST_ACTIVE_SESSION);
    const lastActiveSessionDateStr = getCookieValue(COOKIE_NAMES.LAST_ACTIVE_SESSION_DATE);
    const clientId = getCookieValue(COOKIE_NAMES.CLIENT_ID);

    // Parse the timestamp from cookie (stored as string)
    const lastActiveSessionDate = lastActiveSessionDateStr
        ? parseInt(lastActiveSessionDateStr, 10)
        : null;

    // Log when cross-domain data is retrieved (helps with debugging subdomain issues)
    if (lastActiveSessionId || clientId || lastActiveSessionDate) {
        logger.debug('📍 Retrieved cross-domain tracking data from cookies', {
            hasSessionId: !!lastActiveSessionId,
            hasClientId: !!clientId,
            hasSessionDate: !!lastActiveSessionDate,
            sessionDate: lastActiveSessionDate
                ? new Date(lastActiveSessionDate).toISOString()
                : null,
            currentDomain: window.location.hostname,
        });
    }

    return {
        lastActiveSessionId,
        lastActiveSessionDate,
        clientId,
    };
}

/**
 * Check if we're currently on a different subdomain than where the data was set
 *
 * This is useful for determining when to log cross-domain tracking events.
 *
 * @param originalDomain - The domain from the analytics context (e.g., 'www.example.com')
 * @returns true if we're on a different subdomain
 */
export function isOnDifferentSubdomain(originalDomain?: string): boolean {
    if (!originalDomain) return false;

    const currentHostname = window.location.hostname;
    return currentHostname !== originalDomain;
}
